import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import { create } from 'zustand/react';
import { ILoginResponse } from '@/modules/auth/types/login-response';
import { User } from '@/modules/users/types/user';
import { derivePermissionsFromToken } from '@/modules/users/utils/permissions';

type AuthState = {
  user: User | null;
  token: string | null;
  refreshToken: string | null;

  // handy flags
  hydrated: boolean;

  // actions
  setAuth: (payload: { loginResponse: ILoginResponse }) => void;
  setUser: (user: User) => void;
  logout: () => void;
  setHydrated: (v: boolean) => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const cookieAttrs = () => {
  if (typeof window === 'undefined') {
    return 'Path=/; SameSite=Lax';
  }

  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  const isSecure = window.location.protocol === 'https:' || isLocalhost;

  // Builder/iframes require SameSite=None. Browsers also require Secure for that mode.
  return isSecure ? 'Path=/; SameSite=None; Secure' : 'Path=/; SameSite=Lax';
};

const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${cookieAttrs()}`;
};

const clearCookie = (name: string) => {
  // Intentar limpiar la cookie con todas las combinaciones posibles de atributos
  const clearStrategies = [
    `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT`,
    `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`,
    `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`,
    `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict`,
  ];

  clearStrategies.forEach(strategy => {
    document.cookie = strategy;
  });
};

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      user: null,
      token: null,
      refreshToken: null,
      hydrated: false,
      setAuth: ({ loginResponse }) =>
        set(() => {
          setCookie('token', loginResponse.token);
          if (loginResponse.refreshToken) {
            setCookie('refreshToken', loginResponse.refreshToken);
          } else {
            clearCookie('refreshToken');
          }

          const modules =
            derivePermissionsFromToken(loginResponse.token) ?? loginResponse.user.modules;

          return {
            user: { ...loginResponse.user, modules },
            token: loginResponse.token,
            refreshToken: loginResponse.refreshToken ?? null,
          };
        }),
      setUser: user =>
        set(state => {
          const modules =
            derivePermissionsFromToken(state.token) ?? state.user?.modules ?? user.modules;

          return { user: { ...user, modules } };
        }),
      logout: () =>
        set(() => {
          clearCookie('token');
          clearCookie('refreshToken');
          return { user: null, token: null, refreshToken: null };
        }),
      setHydrated: v => set({ hydrated: v }),
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? window.localStorage : noopStorage,
      ),
      partialize: state => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => state => {
        state?.setHydrated(true);
      },
    },
  ),
);

export const getAuthToken = () => useAuthStore.getState().token;
